import React from 'react';
import LegalPageLayout from '@/components/landing/LegalPageLayout';
import { useI18n } from '@/lib/I18nContext';

const LAST_UPDATED = '12 de junio de 2026';

/* Parses **bold** markers into <strong> elements */
function LegalText({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

function Section({ number, title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-heading font-bold text-gray-900 mb-4" style={{ fontSize: '1.15rem', lineHeight: 1.3 }}>
        {number}. {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function SubSection({ title, children }) {
  return (
    <div className="mb-3">
      <h3 className="font-semibold text-gray-700 mb-2" style={{ fontSize: 14 }}>{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export default function PrivacyPolicy() {
  const { t } = useI18n();
  const P = 'legal.privacy.';

  return (
    <LegalPageLayout title={t('legal.privacy_policy_title', 'Política de Privacidad de Bezkraen')} lastUpdated={LAST_UPDATED}>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-10 text-sm text-amber-800">
        <LegalText text={t(P + 'note', '')} />
      </div>

      {/* Índice */}
      <div className="bg-gray-50 rounded-xl p-5 mb-10 border border-gray-100">
        <h2 className="text-base font-semibold text-gray-800 mb-3" style={{ fontSize: 15 }}>{t(P + 'index_title', 'Índice')}</h2>
        <ol className="text-sm text-gray-500 space-y-1 list-decimal list-inside columns-1 sm:columns-2 gap-x-6" style={{ fontSize: 13 }}>
          {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15].map(n => (
            <li key={n}>{t(P + `s${n}_title`, '')}</li>
          ))}
        </ol>
      </div>

      <Section number={1} title={t(P + 's1_title', '')}>
        <p><LegalText text={t(P + 's1_p1', '')} /></p>

        <SubSection title={t(P + 's1_1_title', '')}>
          <p><LegalText text={t(P + 's1_1_p1', '')} /></p>
          <ul>
            {t(P + 's1_1_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </SubSection>

        <SubSection title={t(P + 's1_2_title', '')}>
          <p><LegalText text={t(P + 's1_2_p1', '')} /></p>
          <p><LegalText text={t(P + 's1_2_p2', '')} /></p>
          <ul>
            {t(P + 's1_2_list1', '').split('|').map((item, i) => <li key={i}><LegalText text={item} /></li>)}
          </ul>
          <p><LegalText text={t(P + 's1_2_p3', '')} /></p>
        </SubSection>

        <SubSection title={t(P + 's1_3_title', '')}>
          <p><LegalText text={t(P + 's1_3_p1', '')} /></p>
          <p><LegalText text={t(P + 's1_3_p2', '')} /></p>
        </SubSection>

        <SubSection title={t(P + 's1_4_title', '')}>
          <p><LegalText text={t(P + 's1_4_p1', '')} /></p>
          <ul>
            {t(P + 's1_4_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </SubSection>

        <SubSection title={t(P + 's1_5_title', '')}>
          <p><LegalText text={t(P + 's1_5_p1', '')} /></p>
          <ul>
            {t(P + 's1_5_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </SubSection>
      </Section>

      <Section number={2} title={t(P + 's2_title', '')}>
        <p><LegalText text={t(P + 's2_p1', '')} /></p>
        <ul>
          {t(P + 's2_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
        </ul>
        <p><LegalText text={t(P + 's2_p2', '')} /></p>
      </Section>

      <Section number={3} title={t(P + 's3_title', '')}>
        <p><LegalText text={t(P + 's3_p1', '')} /></p>
        <p><LegalText text={t(P + 's3_p2', '')} /></p>
        <p><LegalText text={t(P + 's3_p3', '')} /></p>
      </Section>

      <Section number={4} title={t(P + 's4_title', '')}>
        <p><LegalText text={t(P + 's4_p1', '')} /></p>
        <p><LegalText text={t(P + 's4_p2', '')} /></p>
        <p><LegalText text={t(P + 's4_p3', '')} /></p>
      </Section>

      <Section number={5} title={t(P + 's5_title', '')}>
        <p><LegalText text={t(P + 's5_p1', '')} /></p>
        <p><LegalText text={t(P + 's5_p2', '')} /></p>
      </Section>

      <Section number={6} title={t(P + 's6_title', '')}>
        <p><LegalText text={t(P + 's6_p1', '')} /></p>
        <p><LegalText text={t(P + 's6_p2', '')} /></p>
        <ul>
          {t(P + 's6_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
        </ul>
        <p><LegalText text={t(P + 's6_p3', '')} /></p>
      </Section>

      <Section number={7} title={t(P + 's7_title', '')}>
        <p><LegalText text={t(P + 's7_p1', '')} /></p>
        <p><LegalText text={t(P + 's7_p2', '')} /></p>
        <ul>
          {t(P + 's7_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section number={8} title={t(P + 's8_title', '')}>
        <p><LegalText text={t(P + 's8_p1', '')} /></p>
        <p><LegalText text={t(P + 's8_p2', '')} /></p>
      </Section>

      <Section number={9} title={t(P + 's9_title', '')}>
        <p><LegalText text={t(P + 's9_p1', '')} /></p>
        <p><LegalText text={t(P + 's9_p2', '')} /></p>
        <ul>
          {t(P + 's9_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
        </ul>
        <p><LegalText text={t(P + 's9_p3', '')} /></p>
      </Section>

      <Section number={10} title={t(P + 's10_title', '')}>
        <p><LegalText text={t(P + 's10_p1', '')} /></p>
        <p><LegalText text={t(P + 's10_p2', '')} /></p>
        <ul>
          {t(P + 's10_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
        </ul>
        <p><LegalText text={t(P + 's10_p3', '')} /></p>
        <p>
          {t(P + 's10_p4_before', '')}
          <a href="/data-deletion" className="text-blue-600 underline">{t(P + 's10_link_text', '')}</a>
          {t(P + 's10_p4_after', '')}
        </p>
      </Section>

      <Section number={11} title={t(P + 's11_title', '')}>
        <p><LegalText text={t(P + 's11_p1', '')} /></p>
        <ul>
          {t(P + 's11_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
        </ul>
        <p><LegalText text={t(P + 's11_p2', '')} /></p>

        <SubSection title={t(P + 's11_1_title', '')}>
          <p><LegalText text={t(P + 's11_1_p1', '')} /></p>
          <ul>
            {t(P + 's11_1_list1', '').split('|').map((item, i) => <li key={i}><LegalText text={item} /></li>)}
          </ul>
          <p><LegalText text={t(P + 's11_1_p2', '')} /></p>
        </SubSection>

        <SubSection title={t(P + 's11_2_title', '')}>
          <p><LegalText text={t(P + 's11_2_p1', '')} /></p>
          <ul>
            {t(P + 's11_2_list1', '').split('|').map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <p><LegalText text={t(P + 's11_2_p2', '')} /></p>
        </SubSection>

        <SubSection title={t(P + 's11_3_title', '')}>
          <p><LegalText text={t(P + 's11_3_p1', '')} /></p>
        </SubSection>
      </Section>

      <Section number={12} title={t(P + 's12_title', '')}>
        <p><LegalText text={t(P + 's12_p1', '')} /></p>
        <p><LegalText text={t(P + 's12_p2', '')} /></p>
        <p><LegalText text={t(P + 's12_p3', '')} /></p>
      </Section>

      <Section number={13} title={t(P + 's13_title', '')}>
        <p><LegalText text={t(P + 's13_p1', '')} /></p>
      </Section>

      <Section number={14} title={t(P + 's14_title', '')}>
        <p><LegalText text={t(P + 's14_p1', '')} /></p>
        <p><LegalText text={t(P + 's14_p2', '')} /></p>
      </Section>

      <Section number={15} title={t(P + 's15_title', '')}>
        <p><LegalText text={t(P + 's15_p1', '')} /></p>
        <div className="bg-gray-50 rounded-xl p-5 mt-3 border border-gray-100 not-prose">
          <p className="font-semibold text-gray-800" style={{ fontSize: 15 }}>Bezkraen E.I.R.L.</p>
          <p className="text-gray-600" style={{ fontSize: 14 }}>{t('legal.contact.rut_label', '')} 78.504.242-5</p>
          <p className="text-gray-600" style={{ fontSize: 14 }}>{t('legal.contact.address_label', '')} Antonio Bellet 193, Of. 1210, Providencia, Santiago, Chile</p>
          <p className="text-gray-600" style={{ fontSize: 14 }}>{t('legal.contact.contact_email_label', '')} ventas@bezkraen.com</p>
          <p className="text-gray-600" style={{ fontSize: 14 }}>{t('legal.contact.website_label', '')} bezkraen.com</p>
          <p className="text-gray-600" style={{ fontSize: 14 }}>{t('legal.contact.jurisdiction_label', '')} Chile</p>
        </div>
      </Section>
    </LegalPageLayout>
  );
}